import { Box, Fade, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { IconClockBolt } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Container, Heading, Text } from '../atomic';
import { StyledButton } from '../ui';

/**
 * Inline hero background video player.
 * Auto-plays muted and loops. Displays a poster image while loading.
 * Uses both MP4 and WebM sources for broad browser support.
 *
 * @returns {JSX.Element} Video element with poster fallback
 */
function HeroVideo() {
  /** @type {import('react').RefObject<HTMLVideoElement | null>} */
  const videoRef = useRef(null);
  const [hasError, setHasError] = useState(false);

  const handleError = useCallback(() => {
    setHasError(true);
  }, []);

  useEffect(() => {
    /** @type {HTMLVideoElement | null} */
    const video = videoRef.current;
    if (!video) {
      return;
    }

    // Attempt to play; browsers may block autoplay in some contexts
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // Autoplay was prevented — the poster image remains visible
      });
    }
  }, []);

  if (hasError) {
    return (
      <Box
        component="img"
        src="https://rsm-collective-media.s3.us-east-2.amazonaws.com/ella-fin-hero-poster.jpg"
        alt="EllaFin platform preview"
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          borderRadius: '16px',
        }}
      />
    );
  }

  return (
    <Box
      ref={videoRef}
      component="video"
      autoPlay
      muted
      playsInline
      poster="https://rsm-collective-media.s3.us-east-2.amazonaws.com/ella-fin-hero-poster.jpg"
      onError={handleError}
      aria-label="EllaFin platform demonstration video"
      sx={{
        width: '100%',
        height: '100%',
        minHeight: '400px',
        objectFit: 'cover',
        borderRadius: '16px',
        display: 'block',
      }}
    >
      <source
        src="https://rsm-collective-media.s3.us-east-2.amazonaws.com/ella-fin-hero.mp4"
        type="video/mp4"
      />
      <source
        src="https://rsm-collective-media.s3.us-east-2.amazonaws.com/ella-fin-hero.webm"
        type="video/webm"
      />
      {/* Fallback for browsers that do not support video */}
      <img
        src="https://rsm-collective-media.s3.us-east-2.amazonaws.com/ella-fin-hero-poster.jpg"
        alt="EllaFin platform preview"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          borderRadius: '16px',
        }}
      />
    </Box>
  );
}

/**
 * @typedef {Object} HeroSectionNewProps
 * @property {string} [headline] - Main headline text
 * @property {string} [subheadline] - Subheadline text
 * @property {string} [primaryCtaText] - Primary CTA button text
 * @property {string} [primaryCtaHref] - Primary CTA button link
 * @property {string} [secondaryCtaText] - Secondary CTA button text
 * @property {string} [secondaryCtaHref] - Secondary CTA button link
 * @property {Function} [onPrimaryCtaClick] - Primary CTA click handler
 * @property {Function} [onSecondaryCtaClick] - Secondary CTA click handler
 */

/**
 * Hero Section with centered layout and secondary bento strip below.
 *
 * @param {HeroSectionNewProps} props - Component props
 * @returns {JSX.Element} Hero section component
 */
function HeroSectionNew({
  headline,
  subheadline,
  primaryCtaText = 'Schedule a Discovery Call',
  primaryCtaHref = '/early-access#cta',
  secondaryCtaText,
  secondaryCtaHref,
  onPrimaryCtaClick,
  onSecondaryCtaClick,
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const heroRef = useRef(null);
  const theme = useTheme();
  const _isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <>
      {/* Primary Hero */}
      <Box
        ref={heroRef}
        component="section"
        aria-label="Hero section"
        sx={{
          position: 'relative',
          minHeight: { xs: 'auto', md: '90vh' },
          py: { xs: 8, md: 0 },
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          backgroundColor: theme.custom.footerBackground || '#13152b',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              gap: { xs: 4, md: 6 },
              py: { xs: 4, md: 8 },
            }}
          >
            {/* Left side — Text content */}
            <Box
              sx={{
                maxWidth: { xs: '100%', md: '50%' },
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              {/* EllaFin Logo */}
              <Fade in={isLoaded} timeout={600}>
                <Box
                  sx={{
                    mb: 2,
                    textAlign: { xs: 'center', md: 'left' },
                  }}
                >
                  <Box
                    component="img"
                    src="/images/white-ella-green-fin.png"
                    alt="EllaFin"
                    sx={{
                      height: {
                        xs: '28px',
                        sm: '32px',
                        md: '36px',
                        lg: '40px',
                      },
                      width: 'auto',
                      display: 'inline-block',
                    }}
                  />
                </Box>
              </Fade>

              {/* Eyebrow */}
              <Fade in={isLoaded} timeout={700}>
                <Typography
                  variant="overline"
                  sx={{
                    display: 'block',
                    color: theme.palette.secondary.main,
                    fontWeight: 600,
                    letterSpacing: '0.1em',
                    mb: 1.5,
                  }}
                >
                  AWS and Financial Reporting as a Service
                </Typography>
              </Fade>

              {/* Headline */}
              <Fade in={isLoaded} timeout={800}>
                <Box>
                  <Heading
                    variant="h1"
                    component="h1"
                    sx={{
                      fontSize: {
                        xs: '2.25rem',
                        sm: '2.75rem',
                        md: '3.25rem',
                        lg: '3.75rem',
                      },
                      fontWeight: 700,
                      lineHeight: 1.1,
                      mb: 3,
                      color: '#ffffff',
                      letterSpacing: '-0.02em',
                      textAlign: { xs: 'center', md: 'left' },
                    }}
                  >
                    {headline || (
                      <>
                        The outcome of an enterprise{' '}
                        <span style={{ color: theme.palette.secondary.main }}>
                          business intelligence tool
                        </span>
                        , delivered as a service.
                      </>
                    )}
                  </Heading>
                </Box>
              </Fade>

              {/* Subtitle */}
              <Fade in={isLoaded} timeout={1000}>
                <Box>
                  <Text
                    sx={{
                      fontSize: { xs: '1.1rem', md: '1.25rem' },
                      color: alpha('#ffffff', 0.7),
                      lineHeight: 1.7,
                      mb: 2,
                      maxWidth: '700px',
                    }}
                  >
                    {subheadline ||
                      'EllaFin gives Leadership, Finance, and Procurement teams executive-ready AWS billing, dashboards, and financial reporting, defined by you, maintained by RSMC.'}
                  </Text>
                  <Text
                    sx={{
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      color: alpha('#ffffff', 0.55),
                      lineHeight: 1.7,
                      mb: 4,
                      maxWidth: '700px',
                    }}
                  >
                    No new tool to learn. No AWS console to navigate. You define
                    the outcome, and we deliver it in the financial language
                    your business already speaks.
                  </Text>
                </Box>
              </Fade>

              {/* CTA Buttons */}
              <Fade in={isLoaded} timeout={1200}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    justifyContent: { xs: 'center', md: 'flex-start' },
                  }}
                >
                  <StyledButton
                    variant="outlined"
                    size="large"
                    href={primaryCtaHref}
                    onClick={onPrimaryCtaClick}
                    sx={{
                      px: 4,
                      py: 1.75,
                      fontSize: '1.1rem',
                      color: '#ffffff',
                    }}
                  >
                    {primaryCtaText}
                  </StyledButton>
                  {secondaryCtaText && (
                    <StyledButton
                      variant="contained"
                      size="large"
                      href={secondaryCtaHref}
                      onClick={onSecondaryCtaClick}
                      sx={{
                        px: 4,
                        py: 1.75,
                        fontSize: '1.1rem',
                        backgroundColor: '#f4f0eb',
                        color: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: '#ebe5dd',
                        },
                      }}
                    >
                      {secondaryCtaText}
                    </StyledButton>
                  )}
                </Box>
              </Fade>
            </Box>

            {/* Right side — Hero Background Video */}
            <Fade in={isLoaded} timeout={1000}>
              <Box
                sx={{
                  display: { xs: 'none', md: 'block' },
                  flex: '1 1 50%',
                  minHeight: '400px',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <HeroVideo />
              </Box>
            </Fade>
          </Box>
        </Container>
      </Box>

      {/* Secondary Hero — Bento Features Strip */}
      <Box
        component="section"
        aria-label="Key features"
        sx={{
          py: { xs: 6, md: 8 },
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Container maxWidth="lg">
          <Fade in={isLoaded} timeout={1400}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
                alignItems: 'stretch',
              }}
            >
              {/* Left — Large feature card: Data to Dashboards */}
              <Box
                sx={{
                  flex: { xs: '1 1 auto', md: '1 1 50%' },
                  borderRadius: '20px',
                  p: { xs: 3, md: 4 },
                  border: '1px solid #779a0a',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  backgroundImage:
                    'url(https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  transition: 'all 0.3s ease',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: alpha('#779a0a', 0.75),
                    backdropFilter: 'blur(4px)',
                    transition: 'background-color 0.3s ease',
                    zIndex: 0,
                  },
                  '&:hover::before': {
                    backgroundColor: alpha('#779a0a', 0.6),
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 1,
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  <IconClockBolt size={44} stroke={1.5} color="#ffffff" />
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#ffffff',
                    mb: 1,
                    position: 'relative',
                    zIndex: 1,
                    textShadow: '0 1px 4px rgba(0,0,0,0.3)',
                  }}
                >
                  Data to Dashboards in under a week
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: alpha('#ffffff', 0.85),
                    lineHeight: 1.6,
                    maxWidth: '400px',
                    position: 'relative',
                    zIndex: 1,
                    textShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  }}
                >
                  Purpose-built for organizations that live in spreadsheets and
                  want the polished output of an enterprise business
                  intelligence team.
                </Typography>
              </Box>

              {/* Right — Two stacked cards */}
              <Box
                sx={{
                  flex: { xs: '1 1 auto', md: '1 1 50%' },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    borderRadius: '20px',
                    p: { xs: 3, md: 3.5 },
                    border: '1px solid #e0e0e0',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    backgroundImage:
                      'url(https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    transition: 'all 0.3s ease',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: alpha('#ffffff', 0.82),
                      backdropFilter: 'blur(6px)',
                      borderRadius: '20px',
                      transition: 'background-color 0.3s ease',
                      zIndex: 0,
                    },
                    '&:hover::before': {
                      backgroundColor: alpha('#ffffff', 0.72),
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2.5,
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    <Box
                      component="img"
                      src={`${process.env.PUBLIC_URL}/images/badges/aws-qualified-software-badge.png`}
                      alt="AWS Qualified Software badge"
                      sx={{ height: { xs: 72, md: 84 }, width: 'auto' }}
                    />
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                          mb: 0.5,
                        }}
                      >
                        AWS Validated Software
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: '#4a4a4a', lineHeight: 1.5 }}
                      >
                        Built on 100% AWS services
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    borderRadius: '20px',
                    p: { xs: 3, md: 3.5 },
                    border: '1px solid #1e2140',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    backgroundImage:
                      'url(https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    transition: 'all 0.3s ease',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: alpha('#1e2140', 0.7),
                      backdropFilter: 'blur(4px)',
                      borderRadius: '20px',
                      transition: 'background-color 0.3s ease',
                      zIndex: 0,
                    },
                    '&:hover::before': {
                      backgroundColor: alpha('#1e2140', 0.55),
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={`${process.env.PUBLIC_URL}/images/badges/amazon-quicksight-icon.svg`}
                    alt="Amazon Quick Sight"
                    sx={{
                      width: 40,
                      height: 40,
                      mb: 1.5,
                      position: 'relative',
                      zIndex: 1,
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: '#ffffff',
                      mb: 0.5,
                      position: 'relative',
                      zIndex: 1,
                      textShadow: '0 1px 4px rgba(0,0,0,0.3)',
                    }}
                  >
                    Powered by Amazon Quick Sight
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: alpha('#ffffff', 0.85),
                      lineHeight: 1.5,
                      position: 'relative',
                      zIndex: 1,
                      textShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }}
                  >
                    Executive-ready reporting delivered in EllaFin
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>
    </>
  );
}

export default HeroSectionNew;
